// src/features/search/logic.ts
// Framework-agnostic business logic for global search + admin index
// management. Zero React imports — pure TypeScript functions.

import { searchApi } from '@core/api';
import type { SuggestResponse } from '@core/types/search';

export function bytesToHuman(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

export function enumKey(value: string | undefined, prefix: string): string {
  if (!value) return 'unspecified';
  return value.startsWith(prefix) ? value.slice(prefix.length).toLowerCase() : value.toLowerCase();
}

/**
 * Get search suggestions. `searchStore.Suggest()` (the generated store
 * wrapper) drops its parameters entirely — same codegen gap documented for
 * resource.SearchResources and others (docs/ux/resource-ia.md). Call the
 * api layer directly with real arguments instead.
 */
export async function suggest(query: string, limit = 8): Promise<SuggestResponse> {
  if (!query.trim()) return { suggestions: { items: [] } };
  return searchApi.Suggest({ q: query, limit });
}

// Parses a JSON array of SearchDocument-shaped objects for the admin manual
// index tool. Throws with a readable message on malformed input rather than
// a raw JSON.parse error.
export function parseDocumentsJSON(raw: string): Array<Record<string, unknown>> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error('Not valid JSON');
  }
  const arr = Array.isArray(parsed) ? parsed : [parsed];
  for (const doc of arr) {
    if (typeof doc !== 'object' || doc === null || !('entity_urn' in doc) || !('product_slug' in doc)) {
      throw new Error('Each document needs at least entity_urn and product_slug');
    }
  }
  return arr as Array<Record<string, unknown>>;
}
